import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { isEmpty } from "@utils/toolBox";
import { sentenceCase } from "change-case";
import { useConstantsStore } from "@stores/constants";
import { generateCardLabel } from "@utils/generateCardLabel";
import { useSurveyCardsStore } from "@stores/survey/surveyCards";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { computedValuesOptions } from "@utils/computeConditionValuesOptions";
import { getConditionsOverviewText } from "@utils/composables/conditionsOverview";
import cardFieldsDescriptor from "@stores/survey/currentCard/cardFieldsDescriptor";
import { computedAnswerOptions } from "@utils/computeAnswerOptions";
import { Funnel, MapPin, MessageCircle, UserX, Lock } from "lucide-vue-next";

export const useReviewCard = (card) => {
  const hasData = (field) =>
    ["text", "loop", "medium", "media", "custom"].includes(field) ? true : !isEmpty(card?.specs?.[field] ?? []);
  const reviewCardFields = computed(() =>
    // We only want to create components for fields meant for review that are not empty
    cardFieldsDescriptor?.[card?.type].filter(
      (field) =>
        (field?.review !== false && hasData(field?.alias ?? field?.name) && card?.type !== "q_hidden") ||
        (field.name === "q_hidden" && card?.type === "q_hidden"),
    ),
  );
  const reviewCardOptions = computed(() => {
    const filteredOptions = cardOptions.filter(
      (option) =>
        (card?.specs?.[option] != null && card?.specs?.[option] !== false) || !isEmpty(card?.specs?.[option]),
    );
    if (filteredOptions != null) return filteredOptions;
    return [];
  });
  return {
    reviewCardFields,
    reviewCardOptions,
  };
};
export const useReviewCardItemIcons = () => {
  const hasItemConditions = (card) => card?.specs?.item_conditions === true;
  const hasRandomizeItems = (card) => card?.specs?.randomize_items === true;

  const cardSettings = (card) => {
    const settings = [];
    if (hasItemConditions(card)) settings.push("item_conditions");
    if (hasRandomizeItems(card)) settings.push("randomize_items");

    return settings;
  };

  const data = {
    item_conditions: { icon: Funnel, hint: "Conditions" },
    randomize_items: { icon: MapPin, hint: "Fixed" },
  };
  //@deprecated@ 10-05-2024: conditional_display maybe deprecated
  // const isCondition = (item) =>item?.conditional_display && item?.conditions?.length > 0;
  const isCondition = (item) => item?.conditions?.length > 0;

  const settingIcons = (settings, item) => {
    const icons = [];
    if (settings.includes("item_conditions") && isCondition(item)) {
      var details = { ...data.item_conditions };
      details.hint = getConditionsOverviewText(item.conditions, false);
      icons.push(details);
    }

    if (settings.includes("randomize_items") && item?.fixed === true) icons.push(data.randomize_items);

    return icons;
  };

  return {
    cardSettings,
    settingIcons,
  };
};

export const useReviewCardRedirects = () => {
  const redirectTo = (redirect) => generateCardLabel(redirect?.next_card_token, false, false, false);
  return {
    redirectTo,
  };
};

export const useReviewCardConditions = () => {
  const conditionType = (condition) => conditionTypeLabel?.[condition?.type] ?? "";
  const conditionOperator = (condition) => conditionOperatorLabel?.[condition?.operator] ?? "";
  const conditionCardName = (condition) => generateCardLabel(condition?.card_token) ?? condition.condition_name;
  return {
    conditionType,
    conditionOperator,
    conditionCardName,
  };
};

export const useReviewCardAttributes = () => {
  const surveyLocalesStore = useSurveyLocalesStore();
  const { localizeText } = surveyLocalesStore;
  const position = (card) => positionLabel?.[card?.specs?.position] ?? "";
  const loopType = (card) => loopTypeLabel?.[card?.specs?.loop_type] ?? "";
  const questionReference = (card) => generateCardLabel(card?.specs?.source_question_token);
  const quickReplies = (card) => {
    return (card?.specs?.quick_replies ?? [])?.map((qr) => {
      return { title: localizeText(qr?.title) ?? "", destination: generateCardLabel(qr?.next_card_token) ?? "" };
    });
  };
  const buttons = (card) => {
    return (card?.specs?.buttons ?? [])?.map((btn) => {
      return { title: localizeText(btn?.title) ?? "", url: localizeText(btn?.url) ?? "" };
    });
  };
  const populations = (card) => {
    return (card?.specs?.populations ?? [])?.map((pop) => {
      return {
        title: localizeText(pop?.title) ?? "",
        description: localizeText(pop?.description) ?? "",
        size: pop?.size,
        code: pop?.code,
      };
    });
  };
  const images = (card) => {
    const fieldToFetch = card.type === "m_gallery" ? "images" : "answers";
    return (card?.specs?.[fieldToFetch] ?? [])?.map((img) => {
      return { ...img, title: localizeText(img?.title) ?? "", url: localizeText(img?.url) ?? "" };
    });
  };
  const imageUrls = (card) => {
    const fieldToFetch = card.type === "m_gallery" ? "images" : "answers";
    return (card?.specs?.[fieldToFetch] ?? [])?.map((img) => epistoUrl(localizeText(img?.url)) ?? "");
  };
  const epistoUrl = (url) => {
    return url?.replace("episto.b-cdn.net", "cdn.episto.fr");
  };
  return {
    position,
    loopType,
    questionReference,
    quickReplies,
    buttons,
    populations,
    images,
    imageUrls,
    epistoUrl,
  };
};

export const useReviewCardSettingsIcons = () => {
  const hasConditions = (card) => card?.specs?.answer_conditions === true;
  const hasOthers = (card) => card?.specs?.is_other === true;
  const hasScreenoutsHard = (card) => card?.specs?.screenout == true && card?.specs?.screenout_type == "hard";
  const hasScreenoutsSoft = (card) => card?.specs?.screenout == true && card?.specs?.screenout_type == "soft";
  const hasExclusives = (card) => card?.specs?.exclusive_answers === true;
  const hasRandomizeAnswers = (card) => card?.specs?.randomize_answers === true;

  const cardSettings = (card) => {
    const settings = [];
    if (hasConditions(card)) settings.push("conditions");
    if (hasOthers(card)) settings.push("other_specify");
    if (hasScreenoutsHard(card)) settings.push("screenout_hard");
    if (hasScreenoutsSoft(card)) settings.push("screenout_soft");
    if (hasExclusives(card)) settings.push("exclusive");
    if (hasRandomizeAnswers(card)) settings.push("randomize_answers");
    return settings;
  };

  const data = {
    conditions: { icon: Funnel, hint: "Conditions" },
    other_specify: { icon: MessageCircle, hint: "Other, specify" },
    screenout_hard: { icon: UserX, hint: "Screenout hard" },
    screenout_soft: { icon: UserX, hint: "Screenout soft" },
    exclusive: { icon: Lock, hint: "Exclusive" },
    randomize_answers: { icon: MapPin, hint: "Fixed" },
  };
  //@deprecated@ 10-05-2024: conditional_display maybe deprecated
  // const isCondition = (answer) => answer?.conditional_display && answer?.conditions?.length > 0;
  const isCondition = (answer) => answer?.conditions?.length > 0;
  const isOther = (answer) => answer?.is_other === true;
  const isScreenout = (answer) => answer?.screenout === true;
  const isExclusive = (answer) => answer?.exclusive === true;
  const isFixed = (answer) => answer?.fixed === true;

  const settingTags = (settings, answer) => {
    const tags = [];
    if (settings.includes("other_specify") && isOther(answer)) tags.push("other");
    if ((settings.includes("screenout_hard") || settings.includes("screenout_soft")) && isScreenout(answer))
      tags.push("screen out");
    if (settings.includes("exclusive") && isExclusive(answer)) tags.push("exclusive");
    if (settings.includes("randomize_answers") && isFixed(answer)) tags.push("fixed");
    return tags;
  };

  const settingIcons = (settings, answer) => {
    const icons = [];
    if (settings.includes("conditions") && isCondition(answer)) {
      var details = { ...data.conditions };
      details.hint = getConditionsOverviewText(answer.conditions, false);
      icons.push(details);
    }
    if (settings.includes("other_specify") && isOther(answer)) icons.push(data.other_specify);
    if (settings.includes("screenout_hard") && isScreenout(answer)) icons.push(data.screenout_hard);
    if (settings.includes("screenout_soft") && isScreenout(answer)) icons.push(data.screenout_soft);
    if (settings.includes("exclusive") && isExclusive(answer)) icons.push(data.exclusive);
    if (settings.includes("randomize_answers") && isFixed(answer)) icons.push(data.randomize_answers);
    return icons;
  };
  return {
    cardSettings,
    settingIcons,
    settingTags,
    isCondition,
  };
};

export const useReviewCardOptions = () => {
  const optionsContent = ref({});

  const determineOptionsContent = (card) => {
    optionsContent.value = {
      matrix_type: `Matrix type: ${matrixTypeLabel?.[card?.specs?.matrix_type]}`,
      randomize_answers: "Randomize answers",
      randomize_items: "Randomize items",
      has_dynamic_answers: getDynamicAnswersContent(card),
      has_dynamic_items: getDynamicItemsContent(card),
      has_max: `Max. ${card?.specs?.max} answers`,
      set_item_limits: `Max. ${card?.specs?.item_limits} items`,
      seconds: `${card?.specs?.seconds} s`,
      rating_type: `${ratingTypeLabel?.[card?.specs?.rating_type]} rating`,
      rating_limits: `Min ${card?.specs?.rating_limits?.min} - Max ${card?.specs?.rating_limits?.max}`,
      slider_limits: `Min ${card?.specs?.slider_limits?.min} - Max ${card?.specs?.slider_limits?.max}`,
      numeric_limits: `Min ${card?.specs?.numeric_limits?.min} - Max ${card?.specs?.numeric_limits?.max}`,
      display_image_names: "Display image names",
      compact_layout: "Compact style",
      dropdown_input: "Dropdown input",
      multiple_media: "Allow multiple images",
      skippable: "Skip allowed",
      translatable: "Auto-translation",
      allow_decimals: "Allow decimals",
      is_pii: "Personal data",
      randomize_loops: "Randomized loops",
    };
  };
  const getDynamicAnswersContent = (card) => {
    return `${dynamicTypeLabel?.[card?.specs?.dynamic_answers_type] ?? ""} answers from ${generateCardLabel(
      card?.specs?.dynamic_answers_source_question_token,
    )}`;
  };
  const getDynamicItemsContent = (card) => {
    return `${dynamicTypeLabel?.[card?.specs?.dynamic_answers_type] ?? ""} items from ${generateCardLabel(
      card?.specs?.dynamic_items_source_question_token,
    )}`;
  };

  return {
    determineOptionsContent,
    optionsContent,
  };
};

export const useReviewCardCustom = () => {
  const constantsStore = useConstantsStore();
  const { customCardParams } = storeToRefs(constantsStore);
  const { findCardByToken } = useSurveyCardsStore();

  const methodLabel = (card) => sentenceCase(card?.specs?.method_name) ?? "";
  const getValue = (key, value, card) => {
    switch (value.type) {
      case "number":
      case "text":
      case "answer_select":
        return card?.specs?.[key] ?? "";
      case "select":
        return customParamLabels?.[key]?.[card?.specs?.[key]] ?? "";
      case "question_select":
        return typeof card?.specs?.[key] === "string" ? generateCardLabel(card?.specs?.[key]) : card?.specs?.[key];
    }
  };

  const customParams = (card) => {
    let computedParams = [];
    Object.entries(customCardParams.value?.[card?.specs?.method_name]?.params ?? {}).map(([key, value]) => {
      let param = { ...value };
      param.label = value?.display_name || sentenceCase(key) || "";
      param.value = getValue(key, value, card);
      if (value.type === "answer_select")
        param.options = computedValuesOptions(findCardByToken({ token: card?.specs?.question_reference }));
      if (value.type === "question_select" && value.multiple === true) {
        param.options = card?.specs?.[key]?.map((a, i) => {
          return { label: generateCardLabel(a), value: card?.specs?.[key]?.[i] };
        });
      }
      computedParams.push(param);
    });
    return computedParams;
  };
  return {
    methodLabel,
    customParams,
  };
};

export const useReviewCardRecodes = (card) => {
  const surveyCardsStore = useSurveyCardsStore();
  const { findCardByToken } = surveyCardsStore;
  const currentAttributeName = card.specs?.attribute_name;
  const currentRanges = card.specs?.ranges;
  const currentUrlParameter = card.specs?.url_parameter;
  const currentContactAttribute = card.specs?.contact_attribute;
  const currentInputQuestion = generateCardLabel(card?.specs?.input_question?.card_token);

  const currentRangesFromOptions = computed(() => {
    const sourceCard = findCardByToken({ token: card?.specs?.input_question?.card_token });
    return computedAnswerOptions({ answers: sourceCard?.specs?.answers });
  });

  const currentRangesFrom = (range) => {
    if (isEmpty(range)) return "";
    return range.from;
  };

  const currentRangesTo = (range) => {
    if (isEmpty(range)) return "";
    const options = computedAnswerOptions({ answers: card?.specs?.answers });
    const to = options.find((r) => r.value === range.to || r.value === range.value) ?? "";
    return to?.label ?? "";
  };

  const currentRangesMin = (range) => {
    if (isEmpty(range)) return "";
    return range.min;
  };

  const currentRangesMax = (range) => {
    if (isEmpty(range)) return "";
    return range.max;
  };

  return {
    currentAttributeName,
    currentRanges,
    currentRangesFromOptions,
    currentInputQuestion,
    currentUrlParameter,
    currentContactAttribute,
    currentRangesTo,
    currentRangesMin,
    currentRangesMax,
    currentRangesFrom,
  };
};

const cardOptions = [
  "randomize_answers",
  "randomize_items",
  "has_dynamic_answers",
  "has_dynamic_items",
  "display_image_names",
  "compact_layout",
  "dropdown_input",
  "multiple_media",
  "skippable",
  "translatable",
  "slider_limits",
  "numeric_limits",
  "has_max",
  "set_item_limits",
  "seconds",
  "allow_decimals",
  "is_pii",
  "rating_limits",
  "rating_type",
  "randomize_loops",
  "matrix_type",
];
const positionLabel = { start: "Start", end: "End" };
const loopTypeLabel = { answers: "Answers", user_attribute: "A user attribute" };
const ratingTypeLabel = { numeric: "Numeric", stars: "Stars" };
const matrixTypeLabel = { single: "Single choice", multiple: "Multiple choice", rating: "Rating", nps: "NPS" };
const dynamicTypeLabel = { selected: "Selected", unselected: "Unselected", custom: "Custom" };
const conditionTypeLabel = { is: "IS", is_not: "IS NOT", gt: ">", gte: "≥", lt: "<", lte: "≤", between: "BETWEEN" };
const conditionOperatorLabel = { and: "AND", or: "OR" };
const customParamLabels = { order_type: { random: "Random", rank: "Rank", least_filled: "Least filled" } };
